import { FeatureConfig } from './types'
import { vilmers } from './vilmers'
import { dominari } from './dominari'

/**
 * Company feature-config registry. New companies: add a preset file (copy
 * `vilmers.ts`) and register it here under its own key.
 */
export const companies: Record<string, FeatureConfig> = {
  vilmers,
  dominari,
}

/**
 * Resolves the active company from `REACT_APP_COMPANY`, set per-deployment
 * (same invariant as the portal's `NEXT_PUBLIC_THEME`: one deployment = one
 * company, frozen at build time via Vite's `define: { "process.env": ... }`).
 *
 * Unlike the portal's `resolveActiveThemeName` (which throws on an unknown
 * value), an unknown/missing value here falls back to `"vilmers"` — this is
 * an additive, frontend-only UI gate (not a build-breaking brand mismatch),
 * so a misconfigured or absent env var must never break the existing
 * (default) vilmers deployment.
 */
export function resolveActiveCompany(): string {
  const requested = process.env.REACT_APP_COMPANY || 'vilmers'

  if (!(requested in companies)) {
    console.warn(
      `Unknown REACT_APP_COMPANY "${requested}". Falling back to "vilmers". Available companies: ${Object.keys(
        companies
      ).join(', ')}.`
    )
    return 'vilmers'
  }

  return requested
}

export const activeCompany = resolveActiveCompany()

/**
 * Resolved feature config for the active company. Build-time-static (like
 * the portal's `activeTheme`), so a plain module-level const is sufficient —
 * no context/provider needed since it never changes at runtime.
 */
export const features: FeatureConfig = companies[activeCompany]

export type { FeatureConfig }
